import { TYPE_SYNONYMS } from './transactionTypes';

/**
 * Erkennt auto-fixbare Probleme in einer validierten TX.
 * Gibt ein Array von Fix-Vorschlägen zurück: [{ field, suggestedValue, reason, confidence }]
 */
export function computeAutoFixes(tx, issues = []) {
  const fixes = [];

  for (const issue of issues) {
    // Unbekannter Typ, der in TYPE_SYNONYMS steht
    if (issue.level === 'warning' && issue.msg?.startsWith('Unbekannter Typ:')) {
      const raw = issue.msg.replace('Unbekannter Typ:', '').trim().toLowerCase();
      const mapped = TYPE_SYNONYMS[raw];
      if (mapped && mapped !== tx.typ) {
        fixes.push({
          field: 'typ',
          suggestedValue: mapped,
          reason: `"${raw}" → "${mapped}" (bekannter Synonym)`,
          confidence: 'high',
        });
      }
    }

    // Coin-Symbol in falscher Groß-/Kleinschreibung
    if (issue.level === 'warning' && issue.msg?.startsWith('Coin nicht in Datenbank:')) {
      const rawSym = issue.msg.replace('Coin nicht in Datenbank:', '').trim();
      const upper = rawSym.toUpperCase();
      if (upper !== rawSym) {
        fixes.push({
          field: tx.typ === 'swap' ? null : 'coin',
          suggestedValue: upper,
          reason: `"${rawSym}" → "${upper}" (Groß-/Kleinschreibung)`,
          confidence: 'high',
        });
      }
    }

    // Datum liegt in der Zukunft — möglicher Tag/Monat-Swap
    if (issue.level === 'warning' && issue.msg === 'Datum liegt in der Zukunft' && tx.datum) {
      const parts = tx.datum.split('-');
      if (parts.length === 3) {
        const [y, m, d] = parts;
        const swapped = `${y}-${d.padStart(2, '0')}-${m.padStart(2, '0')}`;
        const swappedDate = new Date(swapped);
        if (
          !isNaN(swappedDate) &&
          swappedDate <= new Date() &&
          parseInt(m, 10) <= 31 &&
          parseInt(d, 10) <= 12
        ) {
          fixes.push({
            field: 'datum',
            suggestedValue: swapped,
            reason: `Tag/Monat vertauscht? ${tx.datum} → ${swapped}`,
            confidence: 'low',
          });
        }
      }
    }
  }

  // Deduplizieren: pro Feld nur ersten Fix behalten
  const seen = new Set();
  return fixes.filter(f => {
    if (!f.field || seen.has(f.field)) return false;
    seen.add(f.field);
    return true;
  });
}

/**
 * Wendet einen Fix auf eine TX an und ergänzt repairHistory.
 * Pure Function — mutiert nichts.
 */
export function applyAutoFix(tx, fix) {
  const entry = {
    field: fix.field,
    oldVal: tx[fix.field],
    newVal: fix.suggestedValue,
    at: new Date().toISOString(),
    reason: fix.reason,
  };
  return {
    ...tx,
    [fix.field]: fix.suggestedValue,
    repairHistory: [...(tx.repairHistory || []), entry],
  };
}

/**
 * Wendet eine manuelle Feld-Bearbeitung auf eine TX an und ergänzt repairHistory.
 */
export function applyManualEdit(tx, field, newVal) {
  const entry = {
    field,
    oldVal: tx[field],
    newVal,
    at: new Date().toISOString(),
    reason: 'manual',
  };
  return {
    ...tx,
    [field]: newVal,
    repairHistory: [...(tx.repairHistory || []), entry],
  };
}

/**
 * Gruppiert Issues nach Schweregrad.
 */
export function groupIssuesBySeverity(issues = []) {
  return {
    errors:   issues.filter(i => i.level === 'error'),
    warnings: issues.filter(i => i.level === 'warning'),
  };
}

/**
 * Trägt eine TX eine vom Nutzer gesetzte Klassifikation, die der CSV-Parser
 * NICHT reproduzieren kann?
 *
 * Hintergrund: Der Stale-Parser-Version-Replace-Flow (ImportReviewScreen)
 * löscht bestehende TXs derselben Börse und re-importiert sie frisch, um
 * Parser-Fixes anzuwenden. Dabei gingen manuelle Klassifikationen verloren —
 * konkret fielen die 2×375 HBAR-Auszahlungen „an Freund" (externalTransfer)
 * bei JEDEM Re-Import auf das parser-Default „gesendet" zurück, weil der
 * Parser nicht wissen kann, dass der Empfänger der Bruder war.
 *
 * Solche TXs sind autoritativ vom Nutzer und dürfen vom Replace NIE still
 * überschrieben werden — analog zur bestehenden `source === 'manual'`-Ausnahme.
 *
 * Geschützte Signale (alle vom Parser nicht reproduzierbar):
 *  - userEdited      : explizit über die Edit-Maske gespeichert
 *  - externalTransfer: „Freund/extern"-Klassifikation (sourceMode='external')
 *  - manualLink      : manuell verbundenes Transfer-Paar
 *  - linkedTxId      : Teil eines (auto- oder manuell) gematchten Transfers
 *  - giftFlow        : Geschenk/Bezahlung-Klassifikation (typ-Wechsel)
 */
export function isUserClassified(tx) {
  if (!tx) return false;
  return !!(
    tx.userEdited ||
    tx.externalTransfer ||
    tx.manualLink ||
    tx.linkedTxId ||
    tx.giftFlow
  );
}

/** Alle Coin-Symbole (UPPERCASE) einer TX — deckt Single-Coin- und Swap-TXs ab. */
export function coinsOfTx(tx) {
  if (!tx) return [];
  return [tx.coin, tx.sym, tx.coinIn, tx.coinOut, tx.symIn, tx.symOut]
    .filter(Boolean)
    .map(c => String(c).toUpperCase());
}

/**
 * Darf eine bestehende TX vom Stale-Parser-Version-Replace-Flow gelöscht und
 * frisch re-importiert werden?
 *
 * Der Replace ersetzt NUR, was der neue Import auch tatsächlich wieder liefert.
 * Zwei harte Schutzregeln verhindern Datenverlust:
 *
 *  1. Nutzer-Autorität: manuelle TXs (`source==='manual'`) und vom Nutzer
 *     klassifizierte TXs (isUserClassified) werden nie ersetzt.
 *
 *  2. Coin-Scope: eine TX wird nur ersetzt, wenn ihr Coin im neuen Import
 *     vorkommt. Börsen-Exports sind KEINE Obermenge der Historie — Ledger z.B.
 *     exportiert Assets mit 0-Saldo gar nicht mehr. Ohne diese Regel löschte der
 *     Wipe die gematchte Ledger→Bitget-SUI-Auszahlung (Coin im neuen Export
 *     gar nicht enthalten) → die Bitget-Einzahlung verlor ihren Transfer-
 *     Gegenpart → Phantom-Bestand (45,3 SUI statt 0,0034).
 *
 * @param {object} tx                bestehende TX (Firestore)
 * @param {object} ctx
 * @param {string} ctx.sourceExchange  lowercase Exchange des neuen Imports
 * @param {Set<string>} ctx.importCoins UPPERCASE-Coins, die der neue Import liefert
 * @param {number} ctx.parserVersion    aktuelle PARSER_VERSION
 */
export function isStaleReplaceable(tx, { sourceExchange, importCoins, parserVersion } = {}) {
  if (!tx || !sourceExchange) return false;
  const ex = String(tx.exchange || tx.platform || '').toLowerCase();
  if (ex !== sourceExchange) return false;
  // Regel 1 — Nutzer-Autorität
  if (tx.source === 'manual') return false;
  if (isUserClassified(tx)) return false;
  // Regel 2 — Coin-Scope: nur ersetzen, was der neue Import auch liefert
  if (importCoins && importCoins.size > 0) {
    const exCoins = coinsOfTx(tx);
    if (exCoins.length && !exCoins.some(c => importCoins.has(c))) return false;
  }
  // Pre-versioning (kein Stempel) oder kleinere Version → stale.
  const v = Number(tx.parserVersion);
  return !Number.isFinite(v) || v < parserVersion;
}
